import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { PURGE } from "redux-persist";
import apiInstance from '../../../api';
import axios from 'axios';
import { resolveCurrentServer } from '../../../utils/serverConfig';

// Парсит значение serverTime (число в сек/мс или строку) в миллисекунды.
const parseServerTimeToMs = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;

  const s = String(value).trim();

  // Чисто цифровая строка — возможно timestamp в секундах или миллисекундах
  if (/^\d+$/.test(s)) {
    return s.length === 10 ? Number(s) * 1000 : Number(s);
  }

  // Попробуем распарсить строку вида "YYYY-MM-DD HH:mm:ss" или ISO
  let iso = s.includes('T') ? s : s.replace(' ', 'T');
  // Если нет информации о таймзоне — считаем UTC
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) {
    iso = iso + 'Z';
  }

  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? null : parsed;
};

export const cronHealthRedis = createAsyncThunk(
  'common/cronHealthRedis',
  async (data, { rejectWithValue }) => {
    const api = 'https://sitiy-api.ru/api/cron/health/redis?XDEBUG_SESSION_START=MyVSCode';

    try {
      const response = await axios.post(api, JSON.stringify({ data }));

      return response.data;
    } catch (error) {
      console.error('Ошибка при выполнении запроса:', error);
      return rejectWithValue(error?.response?.data?.message || 'Something went wrong');
    }
  }
);

export const cronHealthDb = createAsyncThunk(
  'common/cronHealthDb',
  async (data, { rejectWithValue }) => {
    const api = 'https://sitiy-api.ru/api/cron/health/db?XDEBUG_SESSION_START=MyVSCode';

    try {
      const response = await axios.post(api, JSON.stringify({ data }));

      return response.data;
    } catch (error) {
      console.error('Ошибка при выполнении запроса:', error);
      return rejectWithValue(error?.response?.data?.message || 'Something went wrong');
    }
  }
);

export const getServerTime = createAsyncThunk(
  'common/getServerTime',
  async (_, { rejectWithValue }) => {
    const api = '/getServerTime?XDEBUG_SESSION_START=MyVSCode';

    try {
      // const response = await axios.get(api);
      const response = await apiInstance.get(api);

      if (response.data.result) {
        return response.data.data.serverTime;
      } else {
        return rejectWithValue('Failed to fetch data');
      }
    } catch (error) {
      console.error('Ошибка при выполнении запроса:', error);
      return rejectWithValue(error?.response?.data?.message || 'Something went wrong');
    }
  }
);

const getInitialCurrentServer = () => {
  try {
    const storedCurrentServer = localStorage.getItem('currentServer');
    const resolvedCurrentServer = resolveCurrentServer(storedCurrentServer);

    if (storedCurrentServer !== resolvedCurrentServer) {
      localStorage.setItem('currentServer', resolvedCurrentServer);
    }

    return resolvedCurrentServer;
  } catch (error) {
    return resolveCurrentServer('');
  }
};

const initialState = {
  globalLoader: true,
  serverTime: null,
  timeDelta: 0,
  isMobile: false,
  orientation: 'portrait',
  isDevMode: false,
  // Текущее серверное подключение (например: https://sitiy-api-test.ru)
  currentServer: getInitialCurrentServer(),
};

export const selectCommonState = state => state.common;

const commonSlice = createSlice({
  name: 'common',
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(getServerTime.fulfilled, (state, action) => { 
        state.serverTime = action.payload;

        // Вычисляем дельту между серверным и клиентским временем (ms)
        const parsed = parseServerTimeToMs(action.payload);
        if (parsed !== null) {
          state.timeDelta = parsed - Date.now();
        }
      })
      .addCase(getServerTime.rejected, (state, action) => {
        console.error('Failed to get server time:', action.payload);
        // Не меняем offset при ошибке, продолжаем использовать предыдущий
      })

      .addCase(PURGE, () => { return initialState; });
  },
  reducers: {
    setGlobalLoader: (state, action) => {
      state.globalLoader = action.payload;
    },
    setDevice: (state, action) => {
      state.isMobile = action.payload;
    },
    setOrientation: (state, action) => {
      state.orientation = action.payload;
    },
    setDevMode: (state, action) => {
      state.isDevMode = action.payload;
    },
    setCurrentServer: (state, action) => {
      state.currentServer = action.payload || '';
      try {
        if (action.payload) {
          localStorage.setItem('currentServer', action.payload);
        } else {
          localStorage.removeItem('currentServer');
        }
      } catch (e) {
        // ignore localStorage errors
      }
    },
    clearCurrentServer: (state) => {
      state.currentServer = '';
      try { localStorage.removeItem('currentServer'); } catch (e) {}
    },
    syncCurrentServerFromStorage: (state, action) => {
      state.currentServer = action.payload;
    },
  },
});

export const {
  setGlobalLoader,
  setDevice,
  setOrientation,
  setDevMode,
  setCurrentServer,
  clearCurrentServer,
  syncCurrentServerFromStorage,
} = commonSlice.actions;

export default commonSlice.reducer;
